import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { safeStorage } from "@/utils/safeStorage";

type ConsentStatus = "pending" | "accepted" | "declined";

interface CookieConsentContextType {
  consentStatus: ConsentStatus;
  acceptCookies: () => void;
  declineCookies: () => void;
  hasAnalyticsConsent: boolean;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

const CONSENT_KEY = "cookie-consent";

// Update Google Analytics consent status
// GA script is loaded in index.html with default denied consent
function updateGoogleConsent(granted: boolean) {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  const consentState = granted ? "granted" : "denied";
  
  window.gtag("consent", "update", {
    analytics_storage: consentState,
    ad_storage: consentState,
    ad_user_data: consentState,
    ad_personalization: consentState,
  });
  
  console.log(`[GA] ✅ Consent updated to: ${consentState}`);
}

export const CookieConsentProvider = ({ children }: { children: ReactNode }) => {
  const [consentStatus, setConsentStatus] = useState<ConsentStatus>("pending");

  // Load saved consent on mount and update GA accordingly
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const savedConsent = safeStorage.getItem(CONSENT_KEY);
    console.log("[CookieConsent] Initializing, saved consent:", savedConsent);
    
    if (savedConsent === "accepted") {
      setConsentStatus("accepted");
      updateGoogleConsent(true);
    } else if (savedConsent === "declined") {
      setConsentStatus("declined");
      // GA already has default denied consent from index.html
    }
  }, []);

  const acceptCookies = () => {
    safeStorage.setItem(CONSENT_KEY, "accepted");
    setConsentStatus("accepted");
    updateGoogleConsent(true);
  };

  const declineCookies = () => {
    safeStorage.setItem(CONSENT_KEY, "declined");
    setConsentStatus("declined");
    updateGoogleConsent(false);
  };

  const hasAnalyticsConsent = consentStatus === "accepted";

  return (
    <CookieConsentContext.Provider
      value={{
        consentStatus,
        acceptCookies,
        declineCookies,
        hasAnalyticsConsent,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error("useCookieConsent must be used within a CookieConsentProvider");
  }
  return context;
};

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}
